import { useViewPort } from "@website/hooks/viewport";
import { MaterialIcon } from "@website/icons/material";
import { Cta, Header, Input, Main } from "@website/ui/datepicker/components";
import { FlatHeader } from "@website/ui/datepicker/components/flat-header/flat-header";
import styles from "@website/ui/datepicker/datepicker.module.scss";
import { DatepickerPropertiesType } from "@website/ui/datepicker/datepicker.types";
import {
  useCalendarLocale,
  usePopover,
  useScroll,
  useShowDate
} from "@website/ui/datepicker/hooks";
import { PopoverFull } from "@website/ui/popover-full";
import { FC, memo, useCallback } from "react";

const Datepicker: FC<DatepickerPropertiesType> = (properties) => {
  const {
    acceptEmpty = false,
    type = "popover",
    error,
    fullWidth,
    helperText,
    mode,
    setMode,
    onChange,
    fromTitle,
    toTitle,
    value,
    setValue,
    changeableMode,
    calendarData,
    size,
    position,
    gapHorizontal,
    gapVertical,
    initialShowDates,
    disableLimit,
    view = "default",
    suggestions,
    noBorder,
    hideIcon,
    inputClassName,
    hideMiddleLine,
    hasDropdownIcon,
    labelClassName,
    onSubmitClick,
    disableDates,
    showInstant,
    isLabelTop,
    initialOpen
  } = properties;

  const { onOpenPopover, onClosePopover, popoverProperties } = usePopover({
    onClick: onSubmitClick,
    initialOpen
  });
  const { scrollReference, setScrollReference, scrolled, scrollToTop } =
    useScroll();

  const {
    calendarLocale,
    changeCalendarLocaleToGregorian,
    changeCalendarLocaleToPersian
  } = useCalendarLocale(properties);
  const { isDesktop } = useViewPort();
  const {
    onNextMonthClick,
    onPreviousMonthClick,
    onShowTodayClick,
    showDates,
    increaseShowDatesNumber,
    nextMonthDisabled,
    previousMonthDisabled
  } = useShowDate(calendarLocale, scrollToTop, initialShowDates, type);

  const onReject = useCallback(() => {
    onChange([], calendarLocale);
  }, [calendarLocale, onChange]);

  return (
    <div className={`${styles["datepicker"]} ${styles["rtl"]}`}>
      {type === "flat" ? (
        <>
          <FlatHeader
            calendarLocale={calendarLocale}
            nextMonthDisabled={nextMonthDisabled}
            previousMonthDisabled={previousMonthDisabled}
            scrolled={scrolled}
            showDates={
              isDesktop && view !== "mobile" ? showDates : [showDates[0]]
            }
            showNextMonth={onNextMonthClick}
            showPreviousMonth={onPreviousMonthClick}
            type={type}
            view={view}
          />
          <Main
            calendarData={calendarData}
            calendarLocale={calendarLocale}
            disableDates={disableDates}
            disableLimit={disableLimit}
            increaseShowDatesNumber={increaseShowDatesNumber}
            mode={mode}
            onChange={onChange}
            scrollReference={scrollReference}
            setScrollReference={setScrollReference}
            setValue={setValue}
            showDates={
              isDesktop && view !== "mobile" ? showDates : [showDates[0]]
            }
            suggestions={suggestions}
            type={type}
            value={value}
            view={view}
            isLabelTop={isLabelTop}
          />
          {showInstant && (
            <div className="border-outline-thin flex gap-1 border-t p-5 pb-0">
              <MaterialIcon className="text-warning" name="bolt" />
              <span className="text-caption">
                در این رزروها بدون نیاز به تایید میزبان، آنی رزرو کنید.
              </span>
            </div>
          )}
        </>
      ) : (
        <>
          <Input
            calendarLocale={calendarLocale}
            changeableMode={changeableMode}
            data-testid="date_picker_input"
            error={error}
            fromTitle={fromTitle}
            fullWidth={fullWidth}
            hasDropdownIcon={hasDropdownIcon}
            helperText={helperText}
            hideIcon={hideIcon}
            hideMiddleLine={hideMiddleLine}
            inputClassName={inputClassName}
            labelClassName={labelClassName}
            mode={mode}
            noBorder={noBorder}
            onOpenPopover={onOpenPopover}
            setMode={setMode}
            size={size}
            toTitle={toTitle}
            value={value}
          />
          <PopoverFull
            {...popoverProperties}
            gapHorizontal={
              isDesktop && view !== "mobile"
                ? gapHorizontal ?? popoverProperties.gapHorizontal
                : "0px"
            }
            gapVertical={
              isDesktop && view !== "mobile"
                ? gapVertical ?? popoverProperties.gapVertical
                : "0px"
            }
            position={position ?? popoverProperties.position}
          >
            <div className={styles["popover"]}>
              <Header
                calendarLocale={calendarLocale}
                changeCalendarLocale={
                  calendarLocale === "persian"
                    ? changeCalendarLocaleToGregorian
                    : changeCalendarLocaleToPersian
                }
                inputProperties={{
                  fromTitle,
                  toTitle,
                  mode,
                  setMode,
                  value,
                  calendarLocale,
                  changeableMode
                }}
                nextMonthDisabled={nextMonthDisabled}
                onClose={onClosePopover}
                previousMonthDisabled={previousMonthDisabled}
                scrolled={scrolled}
                showDates={showDates}
                showNextMonth={onNextMonthClick}
                showPreviousMonth={onPreviousMonthClick}
                showToday={onShowTodayClick}
                type={type}
                view={view}
              />
              <Main
                calendarData={calendarData}
                calendarLocale={calendarLocale}
                disableDates={disableDates}
                disableLimit={disableLimit}
                increaseShowDatesNumber={increaseShowDatesNumber}
                mode={mode}
                onChange={onChange}
                scrollReference={scrollReference}
                setScrollReference={setScrollReference}
                setValue={setValue}
                showDates={showDates}
                suggestions={suggestions}
                type={type}
                value={value}
                view={view}
                isLabelTop={isLabelTop}
              />
              <Cta
                accept={onClosePopover}
                acceptEmpty={acceptEmpty}
                mode={mode}
                reject={onReject}
                value={value}
                view={view}
              />
            </div>
          </PopoverFull>
        </>
      )}
    </div>
  );
};

const DatepickerMemoized = memo(Datepicker);
export { DatepickerMemoized as Datepicker };
